import { type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { EyebrowLabel } from '#app/components/eyebrow-label.tsx'
import { EndCap } from './components/end-cap.tsx'
import { MarketingHero } from './components/marketing-hero.tsx'
import { MarketingSection } from './components/marketing-section.tsx'
import {
	parseWorkoutNotation,
	WorkoutNotationDemo,
} from './components/workout-notation-demo.tsx'

export const EXAMPLES = {
	heroDemo: 'Bench Press, 3x5 @8rpe every 2:30',
	cardioCircuit: `4 Rounds,
  Row, 200m in 0:35
  Run, 2x100m rest 1:00`,
	unitBasic: 'Row, 3x500m',
	unitIntensity: 'Deadlift, 6x6 @90%5RM rest 3:00',
	unitVariability: 'Clean, 4x[4,3,2,2] @70++10%3RM rest 2:30++0:30',
	multiSection: `Squat, 3x5 @80%3RM rest 2:30

Clean, 4x[4,3,2,2] @70%3RM rest 2:30++0:30`,
	rpe: 'Bench Press, 3x5 @8rpe',
	circuit: `4 Rounds,
  Bench Press, 5
  DB Row, 8`,
	synthesis: `Run, 3x100m every 2:00

Squat, 5x3 @85%3RM rest 3:00

4 Rounds, every 2:30
  Bench Press, [5++1] @8rpe
  DB Row, 8 @ 2rir

Hammer Curls, 3x12 @8rpe rest 1:30`,
} as const

export type ExampleName = keyof typeof EXAMPLES

export const loader = async (_args: LoaderFunctionArgs) => {
	return {
		parsed: {
			heroDemo: parseWorkoutNotation(EXAMPLES.heroDemo),
			unitBasic: parseWorkoutNotation(EXAMPLES.unitBasic),
			unitIntensity: parseWorkoutNotation(EXAMPLES.unitIntensity),
			unitVariability: parseWorkoutNotation(EXAMPLES.unitVariability),
			multiSection: parseWorkoutNotation(EXAMPLES.multiSection),
			rpe: parseWorkoutNotation(EXAMPLES.rpe),
			circuit: parseWorkoutNotation(EXAMPLES.circuit),
			cardioCircuit: parseWorkoutNotation(EXAMPLES.cardioCircuit),
			synthesis: parseWorkoutNotation(EXAMPLES.synthesis),
			freeForm: parseWorkoutNotation(''),
		},
	}
}

export const meta: MetaFunction = () => [
	{ title: 'How a workout reads — Momentum' },
	{
		name: 'description',
		content:
			'A walkthrough of the Momentum workout grammar, with live parsed examples.',
	},
]
type ParsedLoaderData = ReturnType<
	typeof useLoaderData<typeof loader>
>['parsed']
export type DemoExamples = Record<
	keyof ParsedLoaderData,
	{ notation: string; parsed: ParsedLoaderData[keyof ParsedLoaderData] }
>

export default function HowToRoute() {
	const { parsed } = useLoaderData<typeof loader>()
	const examples: DemoExamples = Object.entries(EXAMPLES).reduce(
		(prev, [key, value]) => {
			return {
				...prev,
				[key as keyof typeof parsed]: {
					notation: value,
					parsed: parsed[key as keyof typeof parsed],
				},
			}
		},
		{} as DemoExamples,
	)
	return (
		<>
			<MarketingHero
				eyebrow="The language of training"
				heading="The language follows the lift."
				subhead="A workout is a sequence of sections. A section is an exercise or circuit of exercises. The written description mirrors the physical structure of the work."
				ctaLabel=""
				decorations={['grid']}
			>
				<WorkoutNotationDemo
					examples={examples}
					initial="heroDemo"
					caption="Example · Single Exercise"
					heading="Exercise Name, sets`x`reps @intensity rest time"
					description="Try editing the notation. The structured output updates as you type."
				/>
				<WorkoutNotationDemo
					examples={examples}
					initial="synthesis"
					caption="Example · Multiple Sections"
					heading="Build a workout by stacking sections."
					description="Try editing the notation. The structured output updates as you type."
				/>
			</MarketingHero>

			<MarketingSection>
				<EyebrowLabel>Why a grammar?</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					Notation is leverage.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					When coach and athlete write training in the same language, programs
					become readable, comparable, and compoundable — across one athlete or
					a whole roster.
				</p>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>Example 01 · the unit</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					The smallest unit is the exercise-volume pair.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Add intensity, rest, and rep variation for more detailed instruction
					and analysis.
				</p>
				<div className="mt-10 flex flex-col gap-6">
					<WorkoutNotationDemo
						examples={examples}
						initial="unitBasic"
						caption="Basic"
					/>
					<WorkoutNotationDemo
						examples={examples}
						initial="unitIntensity"
						caption="+ Intensity & rest"
					/>
					<WorkoutNotationDemo
						examples={examples}
						initial="unitVariability"
						caption="+ Rep arrays & incrementation"
						description="Rep arrays let each set take a different number of reps. ++10% means intensity grows by 10 percentage points per set; ++0:30 means rest grows by 30 seconds per set."
						rows={3}
					/>
				</div>
			</MarketingSection>

			<MarketingSection>
				<EyebrowLabel>Example 02 · multiple sections</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					A workout is a sequence of sections.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Stack sections to compose a session.
				</p>
				<div className="mt-10">
					<WorkoutNotationDemo
						examples={examples}
						initial="multiSection"
						rows={5}
					/>
				</div>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>Example 03 · intensity by feel</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					RPE is an alternative to percentages.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Rate of Perceived Exertion. Use it when a percentage isn't the right
					tool.
				</p>
				<div className="mt-10">
					<WorkoutNotationDemo examples={examples} initial="rpe" />
				</div>
			</MarketingSection>

			<MarketingSection>
				<EyebrowLabel>Example 04 · circuits</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					Supersets and circuits use the same notation.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					A "Rounds" section loops through its exercises N times.
				</p>
				<div className="mt-10">
					<WorkoutNotationDemo examples={examples} initial="circuit" rows={4} />
				</div>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>Example 05 · synthesis</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">
					A complete workout, composed.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Strength section. Circuit. Accessory. The grammar handles the whole
					session.
				</p>
				<div className="mt-10">
					<WorkoutNotationDemo
						examples={examples}
						initial="synthesis"
						rows={9}
					/>
				</div>
			</MarketingSection>

			<MarketingSection>
				<EyebrowLabel>Example 06 · one notation, any audience</EyebrowLabel>
				<h2 className="mt-5 max-w-3xl font-display text-display-2">
					Whether you write this for one athlete or a roster of fifty, the
					notation is the same.
				</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Composition layers — factors, schedules, group assignment — sit on top
					of the work, not inside it. The grammar travels.
				</p>
			</MarketingSection>

			<MarketingSection tone="ink-soft">
				<EyebrowLabel>Try it</EyebrowLabel>
				<h2 className="mt-5 font-display text-display-2">Write your own.</h2>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-fg-soft">
					Empty canvas. We'll parse as you type. When you want the full
					vocabulary,{' '}
					<Link to="/reference" className="ember-link">
						the language reference
					</Link>{' '}
					indexes every feature.
				</p>
				<div className="mt-10">
					<WorkoutNotationDemo
						examples={examples}
						initial="unitBasic"
						rows={6}
					/>
				</div>
			</MarketingSection>

			<EndCap />
		</>
	)
}
